/**
 * Copyright (c) 2018-2025 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 * @author Jason Pattle <jpattle.exscientia.co.uk>
 * @author Adam Midlik <midlik@gmail.com>
 */
import { MarkerAction } from '../../../mol-util/marker-action.js';
import { PluginStateObject as SO } from '../../../mol-plugin-state/objects.js';
import { lociLabel } from '../../../mol-theme/label.js';
import { PluginBehavior } from '../behavior.js';
import { StateTreeSpine } from '../../../mol-state/tree/spine.js';
import { StateSelection } from '../../../mol-state/index.js';
import { ButtonsType, ModifiersKeys } from '../../../mol-util/input/input-observer.js';
import { Binding } from '../../../mol-util/binding.js';
import { ParamDefinition as PD } from '../../../mol-util/param-definition.js';
import { EmptyLoci, Loci } from '../../../mol-model/loci.js';
import { Bond, Structure, StructureElement, StructureProperties } from '../../../mol-model/structure.js';
import { arrayMax } from '../../../mol-util/array.js';
const B = ButtonsType;
const M = ModifiersKeys;
const Trigger = Binding.Trigger;
//
const DefaultHighlightLociBindings = {
    hoverHighlightOnly: Binding([Trigger(B.Flag.None)], 'Highlight', 'Hover element using ${triggers}'),
    hoverHighlightOnlyExtend: Binding([Trigger(B.Flag.None, M.create({ shift: true }))], 'Extend highlight', 'From selected to hovered element along polymer using ${triggers}'),
};
const HighlightLociParams = {
    bindings: PD.Value(DefaultHighlightLociBindings, { isHidden: true }),
    ignore: PD.Value([], { isHidden: true }),
    preferAtoms: PD.Boolean(false, { description: 'Always prefer atoms over bonds' }),
    mark: PD.Boolean(true)
};
export const HighlightLoci = PluginBehavior.create({
    name: 'representation-highlight-loci',
    category: 'interaction',
    ctor: class extends PluginBehavior.Handler {
        constructor() {
            super(...arguments);
            this.lociMarkProvider = (interactionLoci, action) => {
                if (!this.ctx.canvas3d || !this.params.mark)
                    return;
                this.ctx.canvas3d.mark(interactionLoci, action);
            };
        }
        getLoci(loci) {
            return this.params.preferAtoms && Bond.isLoci(loci) && loci.bonds.length === 2
                ? Bond.toFirstStructureElementLoci(loci)
                : loci;
        }
        register() {
            this.subscribeObservable(this.ctx.behaviors.interaction.hover, ({ current, buttons, modifiers }) => {
                if (!this.ctx.canvas3d || this.ctx.isBusy)
                    return;
                const loci = this.getLoci(current.loci);
                if (this.params.ignore.includes(loci.kind)) {
                    this.ctx.managers.interactivity.lociHighlights.highlightOnly({ repr: current.repr, loci: EmptyLoci });
                    return;
                }
                let matched = false;
                if (Binding.match(this.params.bindings.hoverHighlightOnly, buttons, modifiers)) {
                    // remove repr to highlight loci everywhere on hover
                    this.ctx.managers.interactivity.lociHighlights.highlightOnly({ loci });
                    matched = true;
                }
                if (Binding.match(this.params.bindings.hoverHighlightOnlyExtend, buttons, modifiers)) {
                    // remove repr to highlight loci everywhere on hover
                    this.ctx.managers.interactivity.lociHighlights.highlightOnlyExtend({ loci });
                    matched = true;
                }
                if (!matched) {
                    this.ctx.managers.interactivity.lociHighlights.highlightOnly({ repr: current.repr, loci: EmptyLoci });
                }
            });
            this.ctx.managers.interactivity.lociHighlights.addProvider(this.lociMarkProvider);
        }
        unregister() {
            this.ctx.managers.interactivity.lociHighlights.removeProvider(this.lociMarkProvider);
        }
    },
    params: () => HighlightLociParams,
    display: { name: 'Highlight Loci on Canvas' }
});
//
export const DefaultSelectLociBindings = {
    clickSelect: Binding.Empty,
    clickSelectOnly: Binding.Empty,
    clickToggle: Binding([Trigger(B.Flag.Primary, M.create())], 'Toggle selection', 'Click on element using ${triggers}'),
    clickToggleExtend: Binding([Trigger(B.Flag.Primary, M.create({ shift: true }))], 'Toggle extended selection', 'Click on element using ${triggers} to extend selection along polymer'),
    clickDeselect: Binding.Empty,
    clickDeselectAllOnEmpty: Binding([Trigger(B.Flag.Primary, M.create())], 'Deselect all', 'Click on nothing using ${triggers}'),
};
const SelectLociParams = {
    bindings: PD.Value(DefaultSelectLociBindings, { isHidden: true }),
    ignore: PD.Value([], { isHidden: true }),
    preferAtoms: PD.Boolean(false, { description: 'Always prefer atoms over bonds' }),
    mark: PD.Boolean(true)
};
export const SelectLoci = PluginBehavior.create({
    name: 'representation-select-loci',
    category: 'interaction',
    ctor: class extends PluginBehavior.Handler {
        getLoci(loci) {
            return this.params.preferAtoms && Bond.isLoci(loci) && loci.bonds.length === 2
                ? Bond.toFirstStructureElementLoci(loci)
                : loci;
        }
        applySelectMark(ref, clear) {
            const cell = this.ctx.state.data.cells.get(ref);
            if (cell && SO.isRepresentation3D(cell.obj)) {
                this.spine.current = cell;
                const so = this.spine.getRootOfType(SO.Molecule.Structure);
                if (so) {
                    if (clear) {
                        this.lociMarkProvider({ loci: Structure.Loci(so.data) }, MarkerAction.Deselect);
                    }
                    const loci = this.ctx.managers.structure.selection.getLoci(so.data);
                    this.lociMarkProvider({ loci }, MarkerAction.Select);
                }
            }
        }
        register() {
            const lociIsEmpty = (loci) => Loci.isEmpty(loci);
            const lociIsNotEmpty = (loci) => !Loci.isEmpty(loci);
            const actions = [
                ['clickSelect', current => this.ctx.managers.interactivity.lociSelects.select(current), lociIsNotEmpty],
                ['clickToggle', current => this.ctx.managers.interactivity.lociSelects.toggle(current), lociIsNotEmpty],
                ['clickToggleExtend', current => this.ctx.managers.interactivity.lociSelects.toggleExtend(current), lociIsNotEmpty],
                ['clickSelectOnly', current => this.ctx.managers.interactivity.lociSelects.selectOnly(current), lociIsNotEmpty],
                ['clickDeselect', current => this.ctx.managers.interactivity.lociSelects.deselect(current), lociIsNotEmpty],
                ['clickDeselectAllOnEmpty', () => this.ctx.managers.interactivity.lociSelects.deselectAll(), lociIsEmpty],
            ];
            // sort the action so that the ones with more modifiers trigger sooner.
            actions.sort((a, b) => {
                const x = this.params.bindings[a[0]], y = this.params.bindings[b[0]];
                const k = x.triggers.length === 0 ? 0 : arrayMax(x.triggers.map(t => M.size(t.modifiers)));
                const l = y.triggers.length === 0 ? 0 : arrayMax(y.triggers.map(t => M.size(t.modifiers)));
                return l - k;
            });
            this.subscribeObservable(this.ctx.behaviors.interaction.click, ({ current, button, modifiers }) => {
                if (!this.ctx.canvas3d || this.ctx.isBusy || !this.ctx.selectionMode)
                    return;
                const loci = this.getLoci(current.loci);
                if (this.params.ignore.includes(loci.kind))
                    return;
                // only trigger the 1st action that matches
                for (const [binding, action, condition] of actions) {
                    if (Binding.match(this.params.bindings[binding], button, modifiers) && (!condition || condition(loci))) {
                        action({ repr: current.repr, loci });
                        break;
                    }
                }
            });
            this.ctx.managers.interactivity.lociSelects.addProvider(this.lociMarkProvider);
            this.subscribeObservable(this.ctx.state.events.object.created, ({ ref }) => this.applySelectMark(ref));
            // re-apply select-mark to all representation of an updated structure
            this.subscribeObservable(this.ctx.state.events.object.updated, ({ ref, obj, oldObj, oldData, action }) => {
                const cell = this.ctx.state.data.cells.get(ref);
                if (cell && SO.Molecule.Structure.is(cell.obj)) {
                    const structure = obj.data;
                    const oldStructure = action === 'recreate' ? oldObj === null || oldObj === void 0 ? void 0 : oldObj.data :
                        action === 'in-place' ? oldData : undefined;
                    if (oldStructure &&
                        Structure.areEquivalent(structure, oldStructure) &&
                        Structure.areHierarchiesEqual(structure, oldStructure))
                        return;
                    const reprs = this.ctx.state.data.select(StateSelection.children(ref).ofType(SO.Molecule.Structure.Representation3D));
                    for (const repr of reprs)
                        this.applySelectMark(repr.transform.ref, true);
                }
            });
        }
        unregister() {
            this.ctx.managers.interactivity.lociSelects.removeProvider(this.lociMarkProvider);
        }
        constructor(ctx, params) {
            super(ctx, params);
            this.lociMarkProvider = (reprLoci, action) => {
                if (!this.ctx.canvas3d || !this.params.mark)
                    return;
                this.ctx.canvas3d.mark({ loci: reprLoci.loci }, action);
            };
            this.spine = new StateTreeSpine.Impl(ctx.state.data.cells);
        }
    },
    params: () => SelectLociParams,
    display: { name: 'Select Loci on Canvas' }
});
//
export const DefaultLociLabelProvider = PluginBehavior.create({
    name: 'default-loci-label-provider',
    category: 'interaction',
    ctor: class {
        register() { this.ctx.managers.lociLabels.addProvider(this.f); }
        unregister() { this.ctx.managers.lociLabels.removeProvider(this.f); }
        constructor(ctx) {
            this.ctx = ctx;
            this.f = {
                label: (loci) => {
                    const label = [];
                    if (StructureElement.Loci.is(loci)) {
                        const entityNames = new Set();
                        for (const { unit: u } of loci.elements) {
                            const l = StructureElement.Location.create(loci.structure, u, u.elements[0]);
                            const name = StructureProperties.entity.pdbx_description(l).join(', ');
                            entityNames.add(name);
                        }
                        if (entityNames.size === 1)
                            entityNames.forEach(name => label.push(name));
                    }
                    label.push(lociLabel(loci));
                    return label.filter(l => !!l).join('</br>');
                },
                group: (label) => label.toString().replace(/Model [0-9]+/g, 'Models'),
                priority: 100
            };
        }
    },
    display: { name: 'Provide Default Loci Label' }
});
//
export const DefaultFocusLociBindings = {
    clickFocus: Binding([
        Trigger(B.Flag.Primary, M.create()),
        Trigger(B.Flag.Trigger),
    ], 'Representation Focus', 'Click element using ${triggers}'),
    clickFocusAdd: Binding([
        Trigger(B.Flag.Primary, M.create({ control: true })),
        Trigger(B.Flag.Primary, M.create({ meta: true })),
    ], 'Representation Focus Add', 'Click element using ${triggers}'),
    clickFocusExtend: Binding([
        Trigger(B.Flag.Primary, M.create({ shift: true })),
    ], 'Representation Focus Extend', 'Click on element using ${triggers}'),
    clickFocusSelectMode: Binding([
    // default is empty
    ], 'Representation Focus (Selection Mode)', 'Click element using ${triggers}'),
    clickFocusAddSelectMode: Binding([
    // default is empty
    ], 'Representation Focus Add (Selection Mode)', 'Click element using ${triggers}'),
    clickFocusExtendSelectMode: Binding([
    // default is empty
    ], 'Representation Focus Extend (Selection Mode)', 'Click on element using ${triggers}'),
};
const FocusLociParams = {
    bindings: PD.Value(DefaultFocusLociBindings, { isHidden: true }),
};
export const FocusLoci = PluginBehavior.create({
    name: 'representation-focus-loci',
    category: 'interaction',
    ctor: class extends PluginBehavior.Handler {
        register() {
            this.subscribeObservable(this.ctx.behaviors.interaction.click, ({ current, button, modifiers }) => {
                var _a, _b, _c, _d, _e, _f, _g;
                const { clickFocus, clickFocusAdd, clickFocusExtend, clickFocusSelectMode, clickFocusAddSelectMode, clickFocusExtendSelectMode } = this.params.bindings;
                const binding = this.ctx.selectionMode ? clickFocusSelectMode : clickFocus;
                const matched = Binding.match(binding, button, modifiers);
                const bindingAdd = this.ctx.selectionMode ? clickFocusAddSelectMode : clickFocusAdd;
                const matchedAdd = Binding.match(bindingAdd, button, modifiers);
                const bindingExtend = this.ctx.selectionMode ? clickFocusExtendSelectMode : clickFocusExtend;
                const matchedExtend = Binding.match(bindingExtend, button, modifiers);
                if (!matched && !matchedAdd && !matchedExtend)
                    return;
                // Support snapshot key property, in which case ignore the focus functionality
                const snapshotKey = (_d = (_c = (_b = (_a = current.repr) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.snapshotKey) === null || _c === void 0 ? void 0 : _c.trim()) !== null && _d !== void 0 ? _d : '';
                if (!this.ctx.selectionMode && matched && snapshotKey) {
                    this.ctx.managers.snapshot.applyKey(snapshotKey);
                    return;
                }
                // Support executing markdown commands associated with a visual
                const markdownCommands = (_f = (_e = current.repr) === null || _e === void 0 ? void 0 : _e.props) === null || _f === void 0 ? void 0 : _f.markdownCommands;
                if (!this.ctx.selectionMode && matched && typeof markdownCommands === 'object') {
                    if (Object.keys(markdownCommands).length > 0) {
                        this.ctx.managers.markdownExtensions.tryExecute('click', markdownCommands);
                        return;
                    }
                }
                // only apply structure focus for appropriate granularity
                const { granularity } = this.ctx.managers.interactivity.props;
                if (granularity !== 'residue' && granularity !== 'element')
                    return;
                const loci = Loci.normalize(current.loci, 'residue');
                const entry = this.ctx.managers.structure.focus.current;
                if (entry && Loci.areEqual(entry.loci, loci)) {
                    this.ctx.managers.structure.focus.clear();
                }
                else {
                    if (matched) {
                        this.ctx.managers.structure.focus.setFromLoci(loci);
                    }
                    else {
                        if (matchedExtend) {
                            this.ctx.managers.structure.focus.extendFromLoci(loci);
                        }
                        else { // matchedAdd
                            this.ctx.managers.structure.focus.toggleFromLoci(loci);
                        }
                        // focus-add and focus-extend is not handled in camera behavior, doing it here
                        const current = (_g = this.ctx.managers.structure.focus.current) === null || _g === void 0 ? void 0 : _g.loci;
                        if (current)
                            this.ctx.managers.camera.focusLoci(current);
                    }
                }
            });
        }
    },
    params: () => FocusLociParams,
    display: { name: 'Representation Focus Loci on Canvas' }
});
